import { useState, useEffect, useId, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, X, Loader2, UserX } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { Input } from '@/app/components/ui/input';
import { cn } from '@/app/components/ui/utils';

export interface PersonOption {
  tech_id: number | null;
  person_id: number | null;
  name: string;
}

interface TechResult {
  id: number;
  person_id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  job_title: string;
  email: string;
}

interface PersonSearchComboBoxProps {
  value: PersonOption | null;
  onChange: (person: PersonOption | null) => void;
  className?: string;
  placeholder?: string;
  ineligibleMessage?: string;
  endpoint?: 'techs' | 'persons';
}

export function PersonSearchComboBox({
  value,
  onChange,
  className,
  placeholder = 'Search people by name...',
  ineligibleMessage,
  endpoint = 'techs',
}: PersonSearchComboBoxProps) {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isExternal, setIsExternal] = useState(false);
  const [externalName, setExternalName] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const externalPersonScopeId = useId();

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchEnabled = debouncedSearch.length >= 2 && !value && !isExternal;
  const { data, isFetching } = useQuery({
    queryKey: [endpoint, 'search', debouncedSearch],
    queryFn: async () => {
      if (endpoint === 'persons') {
        const res = await apiClient.get<{
          results: {
            id: number;
            full_name: string;
            email: string;
            has_tech_profile: boolean;
            user_id: number | null;
          }[];
        }>('/api/v1/persons/', {
          params: { q: debouncedSearch, page_size: 10 },
        });
        return res.data;
      }
      const res = await apiClient.get<{
        results: TechResult[];
      }>('/api/v1/crew/techs/', {
        params: { q: debouncedSearch, page_size: 10 },
      });
      return res.data;
    },
    enabled: searchEnabled,
  });

  const results = data?.results ?? [];

  // TODO(tech-debt): This overloads the `tech_id` field with a Person id for
  // foundation-only persons. Clean up once downstream forms unify on person_id.
  const handleSelect = (item: TechResult | { id: number; full_name: string; email: string; has_tech_profile: boolean; user_id: number | null }) => {
    if ('job_title' in item) {
      onChange({ tech_id: item.id, person_id: item.person_id, name: item.full_name });
    } else {
      onChange({ tech_id: item.id, person_id: item.id, name: item.full_name });
    }
    setSearch('');
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange(null);
    setSearch('');
    setIsExternal(false);
    setExternalName('');
  };

  const handleExternalToggle = () => {
    setIsExternal(true);
    setIsOpen(false);
    setSearch('');
  };

  const handleExternalConfirm = () => {
    if (externalName.trim()) {
      onChange({ tech_id: null, person_id: null, name: externalName.trim() });
      setExternalName('');
    }
  };

  if (value) {
    return (
      <div className={cn('mt-1 space-y-1', className)}>
        <div className="flex items-center gap-3 px-3 py-2 border rounded-md bg-gray-50">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium leading-tight">
              {value.name}
              {value.tech_id === null && (
                <span className="text-gray-500 font-normal text-xs ml-1">(external)</span>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={handleClear}
            className="shrink-0 rounded p-0.5 text-gray-400 hover:text-gray-600"
            aria-label="Clear person selection"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {value.tech_id === null && ineligibleMessage && (
          <p className="text-xs text-amber-600">{ineligibleMessage}</p>
        )}
      </div>
    );
  }

  if (isExternal) {
    return (
      <div
        id={externalPersonScopeId}
        ref={containerRef}
        className={cn('mt-1 space-y-1', className)}
      >
        <div className="flex items-center gap-2">
          <Input
            placeholder="Enter external person's name..."
            value={externalName}
            onChange={(e) => setExternalName(e.target.value)}
            data-required="true"
            data-required-empty={!externalName.trim() ? 'true' : 'false'}
            data-required-message="Person name is required."
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleExternalConfirm();
              }
            }}
            className="flex-1"
          />
          <button
            type="button"
            data-validate-required="true"
            data-validation-scope={externalPersonScopeId}
            onClick={handleExternalConfirm}
            className="shrink-0 rounded px-3 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add
          </button>
        </div>
        <button
          type="button"
          onClick={() => setIsExternal(false)}
          className="text-xs text-gray-500 hover:text-gray-700"
        >
          ← Back to search
        </button>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={cn('mt-1 space-y-1 relative', className)}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 pointer-events-none" />
        <Input
          placeholder={placeholder}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          className="pl-10"
        />
      </div>
      {isOpen && search.length >= 2 && (
        <>
          {(isFetching || search !== debouncedSearch) && (
            <p className="text-xs text-gray-400 px-1 py-1 flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" /> Searching...
            </p>
          )}
          {!isFetching && search === debouncedSearch && results.length > 0 && (
            <div className="border rounded-md divide-y max-h-48 overflow-y-auto shadow-sm">
              {results.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="w-full flex items-start gap-3 px-3 py-2.5 text-left text-sm hover:bg-gray-50 transition-colors"
                  onClick={() => handleSelect(item)}
                >
                  <div className="min-w-0">
                    <p className="font-medium truncate">
                      {item.full_name}
                      {'job_title' in item && item.job_title && (
                        <span className="text-gray-500 font-normal"> ({item.job_title})</span>
                      )}
                    </p>
                  </div>
                </button>
              ))}
              <button
                type="button"
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-blue-600 hover:bg-blue-50 transition-colors"
                onClick={handleExternalToggle}
              >
                <UserX className="h-4 w-4" />
                External person (not in system)
              </button>
            </div>
          )}
          {!isFetching && search === debouncedSearch && results.length === 0 && (
            <div className="border rounded-md shadow-sm">
              <p className="text-xs text-gray-500 px-3 py-2">No people found.</p>
              <button
                type="button"
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-blue-600 hover:bg-blue-50 transition-colors border-t"
                onClick={handleExternalToggle}
              >
                <UserX className="h-4 w-4" />
                External person (not in system)
              </button>
            </div>
          )}
        </>
      )}
      {isOpen && search.length > 0 && search.length < 2 && (
        <p className="text-xs text-gray-400 px-1">Type at least 2 characters to search</p>
      )}
    </div>
  );
}
